import {
  DEFAULT_DATE_PATTERN,
  DEFAULT_LOCALE,
  DEFAULT_TIME_FORMAT,
  EMPTY_BRAND_LEGAL_ENTITY,
  NO_ACCENT_OVERRIDE,
  PublicAppSettingsSchema,
} from '@aysar/contracts';
import { useMemo } from 'react';

import { appSettingsApi } from '@/shared/services/app-settings/app-settings.api';

import { type BrandView } from './useBrand.types';

const NO_LINKS: Readonly<Record<string, string>> = Object.freeze({});

/**
 * What a surface knows before the platform has spoken: the registry's own coded
 * defaults, imported from contracts so the two can never disagree (U3) — and
 * never a brand literal (U16). `name` is `''` on purpose: the shell falls back
 * to the neutral product title, not to a guess.
 */
const UNRESOLVED: BrandView = {
  isResolved: false,
  name: '',
  legalEntity: EMPTY_BRAND_LEGAL_ENTITY,
  supportEmail: '',
  links: NO_LINKS,
  logoMediaId: null,
  logoDarkMediaId: null,
  formats: { dateFormat: DEFAULT_DATE_PATTERN, timeFormat: DEFAULT_TIME_FORMAT },
  accentColor: NO_ACCENT_OVERRIDE,
  defaultLocale: DEFAULT_LOCALE,
};

/**
 * The brand, as the shell and every formatter read it (P28).
 *
 * One read over the public `/app-settings` payload, shaped for drawing rather
 * than for the registry's dotted keys. This reads the CACHE and never fetches:
 * `ThemeProvider` — mounted for the app's lifetime, above the login page too —
 * is the one subscription, so forty callers add no requests and no
 * subscriptions, and a page's own data never races the payload. A caller
 * mounted before it lands renders the defaults and re-renders once with the
 * real values. The payload is per-locale (`brand.name` is translatable), and
 * `DirectionProvider` invalidates it on a locale switch.
 *
 * The payload is parsed through the contract here — the client half of the
 * P22c2 leak guard. A body the contract cannot type (a proxy's error page, a
 * deployment a version ahead) is not a brand: the defaults stay, and no
 * formatter is ever handed a value nothing typed.
 */
export function useBrand(): BrandView {
  const { data: raw } = appSettingsApi.endpoints.appSettings.useQueryState();

  return useMemo(() => {
    const parsed = raw === undefined ? null : PublicAppSettingsSchema.safeParse(raw);
    if (parsed === null || !parsed.success) return UNRESOLVED;

    const { data } = parsed;

    return {
      isResolved: true,
      name: data['brand.name'],
      legalEntity: data['brand.legalEntity'],
      supportEmail: data['brand.supportEmail'],
      links: data['brand.links'],
      logoMediaId: data['brand.logoMediaId'],
      logoDarkMediaId: data['brand.logoDarkMediaId'],
      formats: { dateFormat: data['brand.dateFormat'], timeFormat: data['brand.timeFormat'] },
      accentColor: data['brand.accentColor'],
      defaultLocale: data['brand.defaultLocale'],
    };
  }, [raw]);
}
